import { useFormik } from "formik";
import * as Yup from "yup";
import { Customer } from "@/api/customers";
import {
  addUserToCustomer,
  CustomerUserModel,
  CustomerUser,
  TmsCustomerUser,
  updateCustomerUser,
  UpdateCustomerUserModel,
} from "@/api/users";
import { useState, useEffect } from "react";
import {
  MessageBar,
  MessageBarType,
  Overlay,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import { Grid } from "fluentui-react-grid";
import CancelButton from "@/ui-controls/CancelButton";
import SaveButton from "@/ui-controls/SaveButton";
import TextInput from "@/ui-controls/TextInput";

interface Props {
  customer: Customer;
  onClose?: () => any;
  onSave?: (customerUserModel: CustomerUserModel) => any;
  onUpdate?: () => any;
  user?: TmsCustomerUser;
  selectedUser?: CustomerUser | null;
  isEditMode?: boolean;
  onUpdated?: () => any;
  onShowSuccess?: (message: string) => any;
  onShowWarnings?: (messages: string[]) => any;
}

const NewCustomerUserForm = (props: Props) => {
  const [isEditing] = useState<boolean>(!!props.isEditMode);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Auto-clear error message after 8 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const saveCustomerUser = async (model: CustomerUserModel) => {
    setErrorMessage(null);
    setIsSaving(true);

    try {
      if (isEditing && props.selectedUser) {
        const updateModel: UpdateCustomerUserModel = {
          CustomerId: props.customer.Id,
          UserId: props.selectedUser.Id,
          TmsUsername: model.TmsUsername,
        };

        await updateCustomerUser(
          props.customer.Id,
          props.selectedUser.Id,
          updateModel
        );

        const successMsg = `User "${props.selectedUser.EmailAddress}" has been successfully updated.`;
        props.onShowSuccess?.(successMsg);
        props.onUpdated?.();
      } else {
        const result = await addUserToCustomer(props.customer.Id, model);

        // Pass success message to parent to show at screen level
        const successMsg = `User \"${model.DisplayName}\" has been successfully added to the customer.`;
        props.onShowSuccess?.(successMsg);

        // Check for warning messages in the result and pass to parent
        if (
          result?.Messages &&
          Array.isArray(result.Messages) &&
          result.Messages.length > 0
        ) {
          props.onShowWarnings?.(result.Messages);
        }

        if (isEditing) props.onUpdate?.();
        else props.onSave?.(result);
      }

      // Close modal after a brief delay to show success message
      setTimeout(() => {
        props.onClose?.();
      }, 2000);
    } catch (error: any) {
      console.error("Error saving user:", error);
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const clearMessages = () => {
    setErrorMessage(null);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      emailAddress:
        props.selectedUser?.EmailAddress || props.user?.EmailAddress || "",
      username: props.selectedUser?.TmsUsername || props.user?.TmsUsername || "",
      displayName: props.user?.TmsUsername || "",
    },
    validationSchema: Yup.object({
      displayName: props.isEditMode
        ? Yup.string().notRequired()
        : Yup.string().required("Display name is required"),
      emailAddress: props.isEditMode
        ? Yup.string().notRequired()
        : Yup.string()
            .required("User email is required")
            .max(250)
            .email("Must be a valid email address"),
    }),
    onSubmit: (values) => {
      if (isSaving) return; // Prevent multiple submissions

      const model: CustomerUserModel = {
        EmailAddress: values.emailAddress,
        DisplayName: values.displayName,
        CustomerId: props.customer.Id,
        TmsUsername: values.username,
      };
      saveCustomerUser(model);
    },
  });

  const handleError = (error: any) => {
    if (error?.errors) {
      const fieldErrors = Object.values(error.errors) as string[][];
      if (fieldErrors.length && fieldErrors[0].length) {
        setErrorMessage(fieldErrors[0][0]);
        return;
      }
    }
    setErrorMessage("An unexpected error occurred. Please try again.");
    console.error(error);
  };

  return (
    <>
      {" "}
      {/* full-screen overlay when saving */}
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999, // Very high z-index to cover everything
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
            }}
          >
            <Spinner size={SpinnerSize.large} label="Saving user..." />
          </div>
        </div>
      )}
      <form onSubmit={formik.handleSubmit} style={{ paddingTop: 25 }}>
        <Grid dir="ltr">
          {/* client name */}
          {!props.isEditMode && (
            <Grid.Row>
              <Grid.Col sizeSm={12}>
                <TextInput
                  label="Display Name"
                  baseTypeProps={{
                    disabled: isSaving,
                    ...formik.getFieldProps("displayName"),
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      formik.handleChange(e);
                      clearMessages();
                    },
                  }}
                  hasError={
                    !!(formik.touched.displayName && formik.errors.displayName)
                  }
                  errorMessage={formik.errors.displayName}
                />
              </Grid.Col>
            </Grid.Row>
          )}

          {/* contact email */}
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="User Email Address"
                baseTypeProps={{
                  disabled: isSaving || props.isEditMode,
                  ...formik.getFieldProps("emailAddress"),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.handleChange(e);
                    clearMessages();
                  },
                }}
                hasError={
                  !!(formik.touched.emailAddress && formik.errors.emailAddress)
                }
                errorMessage={formik.errors.emailAddress}
              />
            </Grid.Col>
          </Grid.Row>

          {/* username */}
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="TMS Username"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("username"),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.handleChange(e);
                    clearMessages();
                  },
                }}
                hasError={!!(formik.touched.username && formik.errors.username)}
                errorMessage={formik.errors.username}
              />
            </Grid.Col>
          </Grid.Row>

          {/* buttons */}
          <Grid.Row className="panel-buttons">
            <Grid.Col>
              <SaveButton
                text="Save"
                baseTypeProps={{
                  type: "submit",
                  disabled: isSaving,
                }}
              />
            </Grid.Col>
            <Grid.Col>
              <CancelButton
                text="Cancel"
                onClick={() => {
                  clearMessages();
                  props.onClose?.();
                }}
                baseTypeProps={{ disabled: isSaving }}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </form>
      {/* error bar */}
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={() => setErrorMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 10000, // Above overlay
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default NewCustomerUserForm;
